class FaqElement extends HTMLElement {
  constructor() {
    super();
    this.initialise();
  }

  declareVariables() {
    this.KEY_ENTER = 13;
    this.headerHeight = 62;
    this.isMatchesMouseDown = false;
    this.faqs = [];
    this.currentPage = {};
    this.currentCategory = {};
    this.pages = [
      {
        id: "main",
        title: "Feel free to ask us<br>a question.",
        sections: ["pageButtons"],
        categories: [],
      },
      {
        id: "general",
        title: "General.",
        sections: ["faqAccordeon", "icon-general", "back-button-section"],
        categories: [{ id: "general", title: "" }],
      },
      {
        id: "products",
        title: "Products.",
        sections: ["productButtons", "faqAccordeon", "icon-titan", "back-button-section"],
        categories: [
          { id: "keon", title: "KEON" },
          { id: "onyx", title: "Onyx+ And Realm" },
          { id: "pulse", title: "Pulse Interactive" },
          { id: "titan", title: "Titan" },
          { id: "pearl2", title: "Pearl2" },
          { id: "esca2", title: "Esca2" },
          { id: "lumen", title: "Lumen" },
          { id: "cliona", title: "Cliona" },
          { id: "accessories", title: "Accessories" },
        ],
      },
      {
        id: "connection",
        title: "Connection.",
        sections: ["faqAccordeon", "icon-connection", "back-button-section"],
        categories: [{ id: "connection", title: "" }],
      },
      {
        id: "shipping",
        title: "Shipping & Returns.",
        sections: ["faqAccordeon", "icon-shipping", "back-button-section"],
        categories: [{ id: "shipping", title: "" }],
      },
    ];
    this.searchInput = this.querySelector("search-input");
    this.dynamicSections = this.querySelectorAll(".dynamic-section");
    this.titleElement = this.querySelector("#faqTitle");
    this.subtitleElement = this.querySelector("#subTitle");
    this.collapsibles = this.querySelectorAll("div.collapsible");
    for (let i = 0; i < this.collapsibles.length; i++) {
      const collapsible = this.collapsibles[i];
      collapsible["element"] = collapsible.querySelector("collapsible-element");
    }
  }

  initialise() {
    this.declareVariables();
    // Gather all faq data and compile it into an Array
    this.collapsibles.forEach((block) => {
      const id = block.getAttribute("id");
      const category = block.getAttribute("data-category");
      const question = block.querySelector(".collapsible__title").innerHTML;
      const answer = block.querySelector(".collapsible__content").innerHTML;
      const search = `${question} ${answer}`.toLowerCase();
      this.faqs.push({
        id,
        category,
        search,
        question,
        answer,
      });
    });

    const pageParam = new URL(window.location.href).searchParams.get("page");
    if (pageParam) {
      // When someone navigates back to the FAQ from a different location on the website.
      this.setPage(this.findPage(pageParam));
    } else {
      // When the page is directly accessed (through link)
      this.setPage(this.findPage("main"));
      window.history.replaceState({ page: "main" }, "", "?page=main");
    }

    this.subscribe();
  }

  search(terms) {
    let resultArray = [];
    for (let i = 0; i < this.faqs.length; i++) {
      let faq = this.faqs[i];
      for (let j = 0; j < terms.length; j++) {
        let term = terms[j].toLowerCase();
        if (term === "") {
          continue;
        }

        if (faq.search.indexOf(term) !== -1) {
          if (!resultArray.some((s) => s === faq.question)) {
            resultArray.push(faq.question);
          } else {
            break;
          }
        }
      }
    }

    this.searchInput.showResults(resultArray, (question) => {
      this.focusOnQuestion(question);
    });
  }

  /**
   * setCategory
   * @description Sets the given category.
   * @param {String} categoryId ID of the category to be set.
   */
  setCategory(categoryId) {
    const newCategory = this.findCategoryInPage(this.currentPage, categoryId);

    // Add/remove 'faq-selected' css class on the selected category (if applicable).
    this?.querySelector(`button[data-category=${this.currentCategory.id}]`)?.classList.remove(
      "faq-selected",
    );
    this?.querySelector(`button[data-category=${newCategory.id}]`)?.classList.add("faq-selected");

    // Loop through all categories of the current page and give all the unselected
    // ones the class 'faq-unselected'.
    for (const category of this.currentPage.categories) {
      const element = this.querySelector(`button[data-category=${category.id}]`);
      if (newCategory.id === category.id) {
        element.classList.remove("faq-unselected");
      } else {
        element.classList.add("faq-unselected");
      }
    }

    // set the FAQ sub title
    if (newCategory.title) {
      this.subtitleElement.innerHTML = newCategory.title;
      this.subtitleElement.parentNode.style.display = "";
    }

    // Hide and show the FAQs belonging to the chosen category
    this.collapsibles.forEach((block) => {
      if (block.getAttribute("data-category") === newCategory.id) {
        block.classList.remove("hidden");
      } else {
        block.classList.add("hidden");
      }
    });
    this.currentCategory = newCategory;

    // On the 'Products' category focus on the first question when selecting a product.
    const page = this.pages.find((s) => s.id === "products");
    if (page?.categories?.includes(this.currentCategory)) {
      // focus on the subtitle element
      this.subtitleElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  /**
   * setPage
   * @description Set the visible page of the FAQ.
   * @param {Object} page Page to set.
   */
  setPage(page) {
    this.titleElement.innerHTML = page.title;

    // Loop through all the page sections and turn
    // them on/off according to the sections of the given page.
    this.dynamicSections.forEach((section) => {
      const id = section.getAttribute("id");
      if (page.sections.includes(id)) {
        section.classList.remove("hidden");
      } else {
        section.classList.add("hidden");
      }
    });

    // Hide all FAQs on page switch
    this.collapsibles.forEach((block) => {
      block.classList.add("hidden");
      block.element.close(block.element);
    });
    // Clear faq subtitle
    this.subtitleElement.innerHTML = "";
    this.subtitleElement.parentNode.style.display = "none";

    this.currentPage = page;

    // Show all FAQs immediately when there is only 1 category.
    if (page.categories.length === 1) {
      this.collapsibles.forEach((block) => {
        if (block.getAttribute("data-category") === page.categories[0].id) {
          block.classList.remove("hidden");
        } else {
          block.classList.add("hidden");
        }
      });
    }

    // Create a 'fake' history entry in the browser, so we can go back
    // to this state with the back button.
    window.history.pushState({ page: page.id }, "", "?page=" + page.id);
    window.scrollTo(0, 0);
  }

  /**
   * focusOnQuestion
   * @description Sets the right Page and Category, scrolls the page on the given question, and expands it.
   * @param {string} question The question we want to focus on.
   */
  focusOnQuestion(question) {
    const faq = this.faqs.find((s) => s.question === question);
    const faqElement = document.getElementById(faq.id);
    const page = this.findPageByCategoryId(faq.category);

    this.setPage(page);
    if (page.id === "products") {
      this.setCategory(faq.category);
    }

    window.scrollTo({ top: faqElement.offsetTop - this.getHeaderHeight(), behavior: "smooth" });
    faqElement.element.open(faqElement.element);
  }

  /**
   * findCategoryInPage
   * @description Finds the category with the given id in the given page.
   * @param {Object} page Page to search through
   * @param {String} categoryId ID of the category to be found.
   * @returns {Object} The found category or null.
   */
  findCategoryInPage(page, categoryId) {
    for (let i = 0; i < page.categories.length; i++) {
      if (page.categories[i].id === categoryId) {
        return page.categories[i];
      }
    }
    return null;
  }

  /**
   * findPageByCategoryId
   * @description Finds the page that contains the given category id.
   * @param {String} categoryId ID of the category to find.
   * @returns {Object} Found page or null.
   */
  findPageByCategoryId(categoryId) {
    for (let i = 0; i < this.pages.length; i++) {
      for (let j = 0; j < this.pages[i].categories.length; j++) {
        if (this.pages[i].categories[j].id === categoryId) {
          return this.pages[i];
        }
      }
    }
  }

  /**
   * findPage
   * @description Finds the page with the given ID.
   * @param {String} pageId Id of the wanted page
   * @returns {Object} Returns the Page object associated with the given id. Or Null.
   */
  findPage(pageId) {
    return this.pages.find((s) => s.id === pageId);
  }

  /**
   * clearSearchInput
   * @description Clears the text in the search Input and centers it.
   */
  clearSearchInput() {
    this.searchInput.clear();
  }

  /**
   * getHeaderHeight
   * @description Gets the current height of the header.
   * @returns Current Header height in pixels
   */
  getHeaderHeight() {
    const root = document.querySelector(":root");
    const rootStyles = getComputedStyle(root);

    if (screen.width > 1200) {
      return rootStyles.getPropertyValue("--header-height-lg").split("p")[0];
    } else {
      return rootStyles.getPropertyValue("--header-height").split("p")[0];
    }
  }

  /**
   * subscribe
   * @description Subscribes to all necessary events.
   */
  subscribe() {
    // Catch the browser's back function and handle it.
    window.addEventListener("popstate", (backEvent) => {
      const page = this.findPage(backEvent.state?.page);
      if (page && this.currentPage !== page) {
        this.setPage(page);
      }

      this.clearSearchInput();
    });

    const backBtns = this.querySelectorAll(".back-button");
    backBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        this.setPage(this.findPage("main"));
        this.clearSearchInput();
      });
    });

    this.searchInput.addInteractionEventListener((terms) => this.search(terms));

    // Subscription to the 'click' event of the page buttons.
    const pageButtons = this.querySelectorAll(".page-button");
    pageButtons.forEach((pageButton) => {
      pageButton.addEventListener("click", (e) => {
        // Prevent the click event from bubbling.
        e.stopPropagation();
        e.stopImmediatePropagation();

        const pageId = pageButton.getAttribute("data-page");
        const page = this.findPage(pageId);
        this.setPage(page);
      });
    });

    // Subscription to the 'click' event of the different product/category buttons.
    const btns = this.querySelectorAll(".product-button");
    btns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        // Prevent the click event from bubbling.
        e.stopPropagation();
        e.stopImmediatePropagation();

        const categoryId = btn.getAttribute("data-category");
        this.setCategory(categoryId);
      });
    });
  }
}

if (!customElements.get("faq-element")) {
  customElements.define("faq-element", FaqElement);
}

/**
 * Fade in the FAQ section once it's completely ready, this is to prevent FOUC
 * (flash of unstyled content) on pageload.
 */
Promise.allSettled([customElements.whenDefined("faq-element")]).then(() => {
  setTimeout(() => {
    const cont = document.querySelector(".container");
    cont.style.opacity = "";
    cont.classList.add("ready");
  }, 100);
});
